import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For added gloss and water beading, pair it with our hybrid ceramic spray protection.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We bring our own spot-free water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-873" data-mp="28chnvlubynu9">
      <p data-id="element-874" data-mp="1klde4iew9x4y">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-875" data-mp="3az2r5n6cidrz">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-876" data-mp="1ahf1x6qepwj3">Our Exterior Detailing Process</h2>
      <p data-id="element-877" data-mp="ds7hy35frvha">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-878" data-mp="msvx516kg1vr">
        <li className="flex items-start" data-id="element-879" data-mp="mraxwy9b3urk">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-880" data-mp="rrgy52tn6pl9" />
          <span data-id="element-881" data-mp="rpmbnk2q2pf2">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-882" data-mp="238ypf2u1dwqw">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-883" data-mp="21p6xd8omgsim" />
          <span data-id="element-884" data-mp="1wqltmza5vqpf">
            <strong data-id="element-885" data-mp="1wr5cci7myj4i">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-886" data-mp="26nql9ems0edm">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-887" data-mp="26r60chgg9nf1" />
          <span data-id="element-888" data-mp="2kmzzw9vc30fh">
            <strong data-id="element-889" data-mp="2kmhtikeyj5tu">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-890" data-mp="2pf17vjlgy4zp">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-891" data-mp="2pg2ricgjvzt1" />
          <span data-id="element-892" data-mp="33fu3k7nib64e">
            <strong data-id="element-893" data-mp="33flsmlay2avu">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-894" data-mp="3hzoh2dj3jqf9">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-895" data-mp="3n1ds3t6bk43c" />
          <span data-id="element-896" data-mp="3myhvc867jlgh">
            <strong data-id="element-897" data-mp="391cr8dq20wnv">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-898" data-mp="2zahwsm2nmqrc">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-899" data-mp="341zu905gy97k" />
          <span data-id="element-900" data-mp="343b0r7qatv2d">
            <strong data-id="element-901" data-mp="343t5ksr11hlw">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-902" data-mp="2gbvvp7750znf">Why Exterior Detailing is Essential</h2>
      <p data-id="element-903" data-mp="2geibfu46mlgq">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-904" data-mp="19sun9yr2xbib">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-905" data-mp="14ut6xll1fqaj">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and detailing equipment, and we require
        access to a standard electrical outlet at the service location, meaning
        we can perform a flawless exterior detail in your driveway, at your
        office parking lot, or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-906" data-mp="212pfd3kycyvj">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-907" data-mp="20za122z3rtv6">
        <Link to="/services/hybrid-ceramic-spray" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-908" data-mp="251oh6fbe0fir">
          Hybrid Ceramic Spray &rarr;
        </Link>
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-909" data-mp="2jrcmrsqlq0ca">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-910" data-mp="2ss100l867wo4">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}